/**
 * Выезжающая панель. В макете так открываются карточка задачи и настройки доски.
 *
 * §3.4: панель открывается поверх текущего маршрута, но URL обязан отражать
 * открытый объект — за это отвечает вызывающий код, а не сам компонент.
 *
 * Панель выезжает из-за правого края экрана и уезжает туда же. Вызывающий
 * код, который убирает панель из дерева сразу (закрытие = смена маршрута),
 * сначала ставит open={false}, а маршрут меняет в afterLeave — иначе
 * уезжать было бы нечему.
 */

import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Transition } from "@headlessui/react";
import { X } from "lucide-react";
import type { ReactNode } from "react";

import { cn } from "./cn";
import { IconButton } from "./IconButton";

interface SlideOverProps {
  open: boolean;
  onClose: () => void;
  /** Панель уехала за край экрана — теперь её можно убирать из дерева. */
  afterLeave?: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Действия в шапке: копировать номер, меню задачи. */
  actions?: ReactNode;
  width?: "md" | "lg" | "xl";
}

const WIDTHS = {
  md: "max-w-md",
  lg: "max-w-2xl",
  xl: "max-w-4xl",
} as const;

export function SlideOver({
  open,
  onClose,
  afterLeave,
  title,
  children,
  footer,
  actions,
  width = "lg",
}: SlideOverProps) {
  return (
    // appear: панель обычно монтируется уже открытой (карточка задачи —
    // это маршрут), и без него въезд при первом показе не играл бы.
    <Transition show={open} appear {...(afterLeave === undefined ? {} : { afterLeave })}>
      <Dialog onClose={onClose} className="relative z-40">
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-black/30 transition-opacity duration-300 ease-out data-closed:opacity-0 data-leave:duration-200"
        />
        {/* На телефоне панель во всю ширину: полоса слева в 40 px там
            отнимала бы десятую часть экрана (docs/08-mobile-roadmap.md, M2). */}
        <div className="fixed inset-y-0 right-0 flex max-w-full pl-10 max-sm:pl-0">
          <DialogPanel
            transition
            className={cn(
              "flex w-screen flex-col border-l border-line bg-surface shadow-lg",
              // translate-x-full сдвигает на всю ширину панели: у правого
              // края экрана это ровно «за границу», тень тоже уходит.
              "transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
              "data-closed:translate-x-full data-leave:duration-200 data-leave:ease-in",
              WIDTHS[width],
            )}
          >
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3 pt-[calc(0.75rem+var(--safe-top))] max-sm:px-4">
              <DialogTitle className="min-w-0 flex-1 text-base font-extrabold text-fg max-sm:basis-full">
                {title}
              </DialogTitle>
              <div className="flex flex-wrap items-center gap-1 max-sm:w-full max-sm:justify-end">
                {actions}
                <IconButton icon={X} label="Закрыть панель" size="sm" onClick={onClose} />
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>

            {footer && (
              <div className="flex shrink-0 justify-end gap-2 border-t border-line px-5 py-3 pb-[calc(0.75rem+var(--safe-bottom))]">
                {footer}
              </div>
            )}
          </DialogPanel>
        </div>
      </Dialog>
    </Transition>
  );
}
